import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import React, { useState } from 'react';
import { useSnackbarContext } from 'Context/SnackbarContext';
import fr from 'facet-rest';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import classNames from 'classnames';
import Uploader from 'Components/Files/Uploader';
import PersonIcon from '@material-ui/icons/Person';
import MuiCircularProgress from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/CircularProgress';
import AddIcon from '@material-ui/icons/Add';
import PropTypes from 'prop-types';
import { DataValueMap } from 'Helpers/Testing';

const styles = theme => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
    transition: theme.transitions.create('background-image'),
  },
  facetParticlePlaceholder: {
    width: 90,
    height: 90,
    borderRadius: 45,
    overflow: 'hidden',
    background: theme.palette.grey[400],
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  photoSelectorButton: {
    position: 'relative',
    cursor: 'pointer',
  },
  personIcon: {
    color: theme.palette.common.white,
    fontSize: '3.5rem',
  },
  addCircleIconContainer: {
    position: 'absolute',
    right: theme.spacing(-0.75),
    top: 60,
    backgroundColor: theme.palette.common.white,
    borderRadius: theme.spacing(1.5),
  },
  addCircleIcon: {
    color: theme.palette.facet.actionActive,
  },
  noImageAddCircleIcon: {
    top: 136,
    right: theme.spacing(3),
  },
  facetParticleWithImage: {
    width: 90,
    height: 90,
    borderRadius: 45,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    overflow: 'hidden',
  },
  facetParticleLoading: {
    zIndex: 1,
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  facetParticleLoader: {
    color: theme.palette.common.white,
  },
  mutedProfileImage: {
    opacity: 0.7,
  },
  profileImage: {
    height: '100%',
    width: '100%',
    objectFit: 'cover',
  },
  profileImageDoesNotExist: {
    display: 'none',
  },
});

function useSimpleProfileImage({ avatarLink }) {
  const [profileImage, setProfileImage] = useState();

  const clearProfileImage = () => {
    setProfileImage(null);
  };

  const resetProfileImage = () => {
    if (avatarLink) {
      // required to re-render the image
      clearProfileImage();
      setProfileImage(avatarLink.url());
    }
  };

  return {
    profileImage,
    resetProfileImage,
    clearProfileImage,
  };
}


const PhotoUploader = ({
  resource,
  className,
  classes,
}) => {
  const [loading, setLoading] = useState(true);

  const { displayErrorMessage } = useSnackbarContext();

  const [doesImageExist, setDoesImageExist] = useState(false);
  const setImageDoesExist = () => {
    setDoesImageExist(true);
    setLoading(false);
  };
  const setImageDoesNotExist = () => {
    setDoesImageExist(false);
    setLoading(false);
  };

  const profileImageLink = resource.relationship(fr.relationships.profileImage.name)?.link('related');
  const { resetProfileImage, profileImage } = useSimpleProfileImage({
    avatarLink: profileImageLink,
  });

  const fetchProfileImage = () => {
    setLoading(true);
    resetProfileImage();
  };

  useComponentDidMount(() => {
    fetchProfileImage();
  });

  return (
    <Box
      className={classNames(classes.root, className)}
    >
      {profileImageLink ? (
        <Uploader
          url={profileImageLink?.url()}
          successToastMessage="Successfully saved profile image"
          onSuccess={fetchProfileImage}
          onCatch={(err) => {
            displayErrorMessage(err, err.errors);
            setLoading(false);
          }}
          method={'put'}
          accept={'image/x-png,image/png,image/jpeg'}
          params={null}
          onStart={() => setLoading(true)}
        >
          <div
            className={classes.photoSelectorButton}
            test={'onboarding-photo-uploader'}
          >
            {/* Placeholder Icon */}
            {!doesImageExist ? (
              <div
                className={classes.facetParticlePlaceholder}
                test={'onboarding-photo-uploader-placeholder'}
              >
                <PersonIcon className={classes.personIcon} />
              </div>
            ) : null}

            {/* Uploading ..... */}
            {loading ? (
              <div
                className={classes.facetParticleLoading}
              >
                <MuiCircularProgress className={classes.facetParticleLoader} />
              </div>
            ) : null}

            {/* Cropped Profile image */}
            <div
              className={classNames({
                [classes.mutedProfileImage]: loading,
                [classes.facetParticleWithImage]: doesImageExist,
                [classes.profileImageDoesNotExist]: !doesImageExist,
              })}
              test={'onboarding-photo-uploader-profile-image'}
            >
              <img alt="profile" src={profileImage} onLoad={setImageDoesExist} onError={setImageDoesNotExist} className={classes.profileImage} />
            </div>

            {!loading ? (
              <div className={classes.addCircleIconContainer}>
                <AddIcon
                  className={classNames(classes.addCircleIcon, {
                    [classes.noImageAddCircleIcon]: !profileImage,
                  })}
                  {...DataValueMap.init('onboarding-photo-uploader-add-icon').build()}
                />
              </div>
            ) : null}
          </div>
        </Uploader>) : null}
    </Box>
  );
};
PhotoUploader.defaultProps = {
  className: '',
};

PhotoUploader.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  resource: PropTypes.object.isRequired,
};
export default withStyles(styles)(PhotoUploader);
